import React, { useEffect, useState } from "react";
import { Mail, Palette, Save, Send, Loader2, Eye } from "lucide-react";
import { getEmailTemplate, updateEmailTemplate, sendTestEmail } from "../../api";
import { useToast } from "../../hooks/use-toast";

const input = "w-full h-[44px] px-3 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:ring-2 focus:ring-[#b8935f]/30 focus:border-[#b8935f]";

const EmailPreview = ({ t }) => (
  <div className="rounded-xl overflow-hidden border border-[#f0ece5]" style={{ background: "#f4f1ec", padding: "24px", fontFamily: "Georgia, 'Times New Roman', serif" }} data-testid="email-preview">
    <div style={{ maxWidth: 480, margin: "0 auto", background: "#fff", borderRadius: 14, overflow: "hidden" }}>
      <div style={{ background: "#1c1c1e", padding: "20px 28px" }}>
        <span style={{ color: t.accent, fontSize: 18, fontWeight: 700, letterSpacing: 1 }}>{t.brandName || "ANADEAR ESTATES"}</span>
        {t.tagline ? <div style={{ color: "#fff", opacity: 0.6, fontSize: 11, letterSpacing: 0.5, marginTop: 3 }}>{t.tagline}</div> : null}
      </div>
      <div style={{ padding: 28 }}>
        <h1 style={{ margin: "0 0 12px", fontSize: 20, color: "#1c1c1e", fontWeight: 400 }}>Thanks for your enquiry</h1>
        <p style={{ fontSize: 14, color: "#4a4a4a", lineHeight: 1.6, margin: 0 }}>
          Hi Jane,<br /><br />Thank you for getting in touch with us. I'd love to help you with the next step — are you free for a quick call this week?
        </p>
        <a href="#" onClick={(e) => e.preventDefault()} style={{ display: "inline-block", marginTop: 16, background: t.accent, color: "#fff", textDecoration: "none", padding: "11px 24px", borderRadius: 8, fontSize: 14, fontWeight: 600 }}>Browse our listings</a>
        {t.signature ? <p style={{ fontSize: 14, color: "#4a4a4a", lineHeight: 1.6, marginTop: 18, whiteSpace: "pre-line" }}>{t.signature}</p> : null}
      </div>
      <div style={{ padding: "16px 28px", background: "#faf7f2", borderTop: "1px solid #efe9df", color: "#9a9a9a", fontSize: 12 }}>
        {t.footer || "Anadear Estates · Bath"}
      </div>
    </div>
  </div>
);

const AdminEmailTemplate = () => {
  const { toast } = useToast();
  const [t, setT] = useState(null);
  const [saving, setSaving] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => { getEmailTemplate().then(setT).catch(() => setT({ accent: "#b8935f", brandName: "ANADEAR ESTATES", tagline: "", signature: "", footer: "Anadear Estates · Bath · This is an automated message." })); }, []);

  const save = async () => {
    setSaving(true);
    try { const saved = await updateEmailTemplate(t); setT(saved); toast({ title: "Email template saved", description: "All outgoing emails now use this branding." }); }
    catch (e) { toast({ title: "Couldn't save", description: e?.response?.data?.detail || "" }); }
    finally { setSaving(false); }
  };
  const sendTest = async () => {
    if (!testTo.trim()) { toast({ title: "Enter an email address to send the test to" }); return; }
    setTesting(true);
    try { await sendTestEmail(testTo.trim()); toast({ title: "Test email sent", description: `Check ${testTo}` }); }
    catch (e) { toast({ title: "Couldn't send test", description: e?.response?.data?.detail || "Verify your Resend domain first." }); }
    finally { setTesting(false); }
  };

  if (!t) return <div className="p-10 text-center text-[#8a8a8a]">Loading…</div>;
  const S = (k) => (e) => setT({ ...t, [k]: e.target.value });

  return (
    <div className="bg-white rounded-2xl border border-[#f0ece5] p-6" data-testid="admin-email-template">
      <h3 className="font-display text-[22px] font-400 text-[#2b2b2b] mb-1 flex items-center gap-2"><Mail size={20} className="text-[#b8935f]" /> Email Template</h3>
      <p className="text-[13px] text-[#8a8a8a] mb-6">Brand every outgoing email — CRM follow-ups, agreements, maintenance updates and owner statements all use these settings.</p>
      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-4">
          <div>
            <label className="text-[13px] text-[#6a6a6a] mb-1 block flex items-center gap-1.5"><Palette size={14} /> Brand accent colour</label>
            <div className="flex items-center gap-2">
              <input type="color" value={t.accent || "#b8935f"} onChange={S("accent")} className="w-12 h-11 rounded-lg border border-[#e2d8c8] cursor-pointer" data-testid="template-accent-color" />
              <input value={t.accent || ""} onChange={S("accent")} className={input} data-testid="template-accent-hex" />
            </div>
          </div>
          <div><label className="text-[13px] text-[#6a6a6a] mb-1 block">Header brand text</label><input value={t.brandName || ""} onChange={S("brandName")} className={input} data-testid="template-brand" /></div>
          <div><label className="text-[13px] text-[#6a6a6a] mb-1 block">Header tagline (optional)</label><input value={t.tagline || ""} onChange={S("tagline")} placeholder="Bath's independent estate agency" className={input} data-testid="template-tagline" /></div>
          <div><label className="text-[13px] text-[#6a6a6a] mb-1 block">Email signature</label><textarea value={t.signature || ""} onChange={S("signature")} rows={4} placeholder={"Warm regards,\nThe Anadear Estates team\n+44 7754 915190"} className="w-full px-3 py-2 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:border-[#b8935f] resize-none" data-testid="template-signature" /></div>
          <div><label className="text-[13px] text-[#6a6a6a] mb-1 block">Footer text</label><textarea value={t.footer || ""} onChange={S("footer")} rows={2} className="w-full px-3 py-2 rounded-lg border border-[#e2d8c8] text-[14px] outline-none focus:border-[#b8935f] resize-none" data-testid="template-footer" /></div>
          <div className="flex items-center gap-2 pt-1">
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-5 h-[46px] rounded-lg bg-[#b8935f] text-white text-[14px] font-500 hover:bg-[#a67f4d] disabled:opacity-60" data-testid="template-save">{saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save template</button>
          </div>
          <div className="pt-4 border-t border-[#f0ece5]">
            <label className="text-[13px] text-[#6a6a6a] mb-1 block">Send a test email</label>
            <div className="flex items-center gap-2">
              <input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="you@example.com" className={input} data-testid="template-test-email" />
              <button onClick={sendTest} disabled={testing} className="inline-flex items-center gap-2 px-4 h-[44px] rounded-lg border border-[#e2d8c8] text-[#6a6a6a] text-[13px] font-500 hover:border-[#b8935f] hover:text-[#b8935f] disabled:opacity-60 whitespace-nowrap" data-testid="template-send-test">{testing ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Send test</button>
            </div>
            <p className="text-[12px] text-[#a0a0a0] mt-2">Requires a verified Resend domain to deliver.</p>
          </div>
        </div>
        <div>
          <p className="text-[12px] uppercase tracking-wide text-[#a0a0a0] mb-2 flex items-center gap-1.5"><Eye size={13} /> Live preview</p>
          <EmailPreview t={t} />
        </div>
      </div>
    </div>
  );
};

export default AdminEmailTemplate;
